import { Box, Flex, Grid, SimpleGrid, Skeleton, SkeletonCircle, SkeletonText, VStack } from "@chakra-ui/react"

// Define colors for consistency with the main component
const colors = {
    adminbg: "#EFF3F6",
    LgColor: "#39465C",
    textBlue: "#174EA6",
    fontBlue: "#1A73E8",
    lightGray: "#F3F4F6",
    darkGray: "#4B5563",
}

export function CompanyProfileSkeleton() {
    return (
        <div className="p-3 bg-mainBg" style={{ fontFamily: "Inter, sans-serif" }}>
            <div className="sm:px-4 px-2 min-h-screen">
                <div className="rounded-lg">
                    <div className="flex flex-col gap-3 mb-6">
                        {/* LinkedIn-style Profile Section Skeleton */}
                        <div className="grid grid-cols-1 md:grid-cols-[60%_40%] gap-2">
                            {/* Left Section - Profile Skeleton */}
                            <div className="relative">
                                {/* Cover Image Skeleton */}
                                <Skeleton h="20" w="full" roundedTop="lg" />
                                {/* Profile Section Skeleton */}
                                <div className="rounded-b-lg bg-white">
                                    <div className="px-4 py-2">
                                        {/* Profile Picture Skeleton */}
                                        <div className="relative -mt-20 mb-4">
                                            <SkeletonCircle size="40" border="4px solid white" />
                                        </div>
                                        {/* Profile Info Skeleton */}
                                        <SkeletonText mt="4" noOfLines={1} spacing="4" skeletonHeight="4" width="70%" />
                                        {/* Contact Info Skeleton */}
                                        <SimpleGrid columns={1} spacing={4} mt={6} mb={2}>
                                            {Array.from({ length: 4 }).map((_, i) => (
                                                <Flex key={i} alignItems="center" gap={3}>
                                                    <SkeletonCircle size="6" />
                                                    <SkeletonText noOfLines={1} skeletonHeight="3" width="80%" />
                                                </Flex>
                                            ))}
                                        </SimpleGrid>
                                    </div>
                                </div>
                            </div>
                            {/* Right Section - Call to Action Skeleton */}
                            <Box
                                p={4}
                                borderRadius="lg"
                                boxShadow="xl"
                                className="bg-gradient-to-br from-[#1565C0] via-[#1E88E5] to-[#26C6DA]"
                            >
                                <VStack align="stretch" spacing={2}>
                                    <Flex align="start" justify="space-between">
                                        <Flex align="start">
                                            <SkeletonCircle size="8" mr={2} />
                                            <VStack align="start" spacing={0}>
                                                <SkeletonText noOfLines={1} skeletonHeight="4" width="150px" />
                                                <SkeletonText noOfLines={1} skeletonHeight="3" width="100px" mt={1} />
                                            </VStack>
                                        </Flex>
                                        <Skeleton px={2.5} py={1} borderRadius="full" width="100px" height="24px" />
                                    </Flex>
                                    <Grid
                                        templateColumns={{ base: "repeat(2, 1fr)", sm: "repeat(3, 1fr)", md: "repeat(4, 1fr)" }}
                                        gap={2.5}
                                    >
                                        {Array.from({ length: 4 }).map((_, i) => (
                                            <Box
                                                key={i}
                                                p={2.5}
                                                borderRadius="lg"
                                                bg="whiteAlpha.150"
                                                border="1px solid"
                                                borderColor="whiteAlpha.200"
                                            >
                                                <Flex justify="center" mb={1}>
                                                    <SkeletonCircle size="6" />
                                                </Flex>
                                                <SkeletonText noOfLines={1} skeletonHeight="4" width="60%" mx="auto" mb={0.5} />
                                                <SkeletonText noOfLines={1} skeletonHeight="3" width="80%" mx="auto" />
                                            </Box>
                                        ))}
                                    </Grid>
                                    <Skeleton height="48px" width="full" borderRadius="xl" />
                                </VStack>
                            </Box>
                        </div>
                        {/* Job Statistics section Skeleton */}
                        <Box bg="white" p={6} borderRadius="lg" boxShadow="sm">
                            <SkeletonText noOfLines={1} skeletonHeight="5" width="50%" mx="auto" mb={6} />
                            <Grid templateColumns={{ base: "1fr", lg: "1fr 1fr" }} gap={6}>
                                <Box bg={colors.lightGray} p={2} borderRadius="lg">
                                    <SkeletonText noOfLines={1} skeletonHeight="4" width="40%" mx="auto" mb={4} />
                                    <Skeleton height="250px" width="full" />
                                </Box>
                                <Box bg={colors.lightGray} p={2} borderRadius="lg">
                                    <SkeletonText noOfLines={1} skeletonHeight="4" width="40%" mx="auto" mb={4} />
                                    <Skeleton height="250px" width="full" />
                                </Box>
                            </Grid>
                            {/* Summary Cards Row Skeleton */}
                            <Grid
                                templateColumns={{ base: "repeat(2, 1fr)", sm: "repeat(3, 1fr)", md: "repeat(5, 1fr)" }}
                                gap={3}
                                mt={6}
                            >
                                {Array.from({ length: 8 }).map((_, i) => (
                                    <Box key={i} p={3} borderRadius="md" border={`1px solid ${colors.lightGray}`} textAlign="center">
                                        <Flex justifyContent="center" alignItems="center" mb={1.5}>
                                            <SkeletonCircle size="4" />
                                        </Flex>
                                        <SkeletonText noOfLines={1} skeletonHeight="4" width="50%" mx="auto" mb={0.5} />
                                        <SkeletonText noOfLines={1} skeletonHeight="3" width="70%" mx="auto" />
                                    </Box>
                                ))}
                            </Grid>
                        </Box>
                        {/* Tabs Section Skeleton */}
                        <Box bg="white" p={6} borderRadius="lg" boxShadow="sm">
                            <Flex mb={4}>
                                <Skeleton width="80px" height="30px" mr={4} />
                                <Skeleton width="80px" height="30px" />
                            </Flex>
                            <Skeleton height="200px" width="full" />
                        </Box>
                    </div>
                </div>
            </div>
        </div>
    )
}
